import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuCheck, LuTriangleAlert } from "react-icons/lu";
import { getToken } from "../../../common/environment_config.es6";
import { approveJob } from "../../../common/server_utils.es6";
import { mapAiProviderErrorMessage } from "../../../common/ai_provider_errors.es6";
import { addSound, editSound } from "../../../common/actions";

// Background tracker for the "Tüm sahneler" (narrate-all-scenes) job.
//
// AIVoiceTab fires a `narrateAllScenesStarted` window event right after the API
// returns 202 + jobId, then closes the modal. This component (mounted once in
// EditorModals, which outlives the modal) listens for that event, polls
// GET {baseUrl}api/jobs/{jobId} until the job finishes, and on completion reads
// the freshly generated audio URLs straight from the job result
// (result.audios = [{ navId, url }]) and merges them into the Redux soundList —
// only for the narrated scenes, so unrelated local edits are preserved. A small
// badge shows progress; a final toast reports the outcome.

export const NARRATE_ALL_EVENT = "narrateAllScenesStarted";

const POLL_INTERVAL = 3000;
const DONE_DISMISS_MS = 8000;
// Backend job timeout is 30 min; give up a bit past that so a wrong job-base URL
// or a stuck job doesn't poll forever.
const MAX_POLL_ATTEMPTS = 800; // ~40 min at 3s

// Probe an audio URL for its duration (whole seconds). Best-effort: resolves 0
// on any failure so a missing/blocked metadata read never blocks the merge.
const measureAudioDuration = (url) =>
  new Promise((resolve) => {
    if (!url || typeof window === "undefined") {
      resolve(0);
      return;
    }
    let done = false;
    const el = document.createElement("audio");
    el.preload = "metadata";
    const finish = (v) => {
      if (done) return;
      done = true;
      resolve(v && isFinite(v) && !isNaN(v) ? Math.round(v) : 0);
    };
    el.onloadedmetadata = () => finish(el.duration);
    el.onerror = () => finish(0);
    setTimeout(() => finish(0), 8000);
    el.src = url;
  });

// The generic job-status endpoint (GET api/jobs/{id}) lives on MW_API_URL
// (localhost:4000 in dev) — the same host the avatar tab polls — NOT on
// ideaLibraryRootUrl, where the api/ideal-studio/* narrate POST goes.
const resolveJobsBase = (fallback) => {
  const b = process.env.MW_API_URL || fallback || "";
  return b.endsWith("/") ? b : `${b}/`;
};

// API provider value -> editor's UI service value stored in voiceData.provider.
const apiProviderToService = (p) => {
  if (p === "azure") return "azure";
  if (p === "elevenlabs") return "elevenlabs";
  if (p && p.startsWith("gemini")) return "google";
  return p || "";
};

const NarrationJobTracker = ({ store, dispatch }) => {
  // { [jobId]: { jobId, baseUrl, trainingId, scenes, provider, voice, language,
  //              status, progress, total, applied, failedCount, error } }
  const [jobs, setJobs] = useState({});
  const timersRef = useRef({});
  const startedRef = useRef(new Set());
  const mountedRef = useRef(true);

  const patchJob = useCallback((jobId, patch) => {
    setJobs((prev) => (prev[jobId] ? { ...prev, [jobId]: { ...prev[jobId], ...patch } } : prev));
  }, []);

  const removeJob = useCallback((jobId) => {
    setJobs((prev) => {
      if (!prev[jobId]) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
  }, []);

  // Merge the freshly generated audio (job.result.audios = [{ navId, url }])
  // into the Redux soundList — only for the narrated scenes. Returns applied count.
  const applyResults = useCallback(
    async (job) => {
      const audios = Array.isArray(job.result?.audios) ? job.result.audios : [];
      if (audios.length === 0) {
        console.warn("[NarrationJobTracker] job result has no audios array; nothing to merge");
        return 0;
      }

      const textByNav = {};
      (job.scenes || []).forEach((s) => {
        if (s && s.navId) textByNav[s.navId] = s.text;
      });
      const service = apiProviderToService(job.provider);

      // Probe durations up front, in parallel.
      const durations = await Promise.all(
        audios.map((a) => (a && a.url ? measureAudioDuration(a.url) : Promise.resolve(0)))
      );

      let applied = 0;
      audios.forEach((a, i) => {
        if (!a || !a.navId || !a.url) return;
        const navId = a.navId;

        const present = store.getState()?.undoGroup?.present?.soundList?.[navId] || [];
        // Replace the existing AI narration entry (has voiceData/narrationText)
        // in place. On a freshly-loaded training those fields can be missing, so
        // if there's exactly one sound on the scene, treat it as the narration.
        // Only when there are multiple unidentifiable sounds do we add a new one.
        let existingIdx = present.findIndex((s) => s && (s.voiceData || s.narrationText));
        if (existingIdx < 0 && present.length === 1) existingIdx = 0;
        const existing = existingIdx >= 0 ? present[existingIdx] : null;

        const startTime = existing?.startTime || 0;
        const duration =
          durations[i] ||
          (existing && existing.endTime != null
            ? existing.endTime - (existing.startTime || 0)
            : 0) ||
          0;

        const soundData = {
          file: a.url,
          name: existing?.name || `narration-${navId}.mp3`,
          startTime,
          endTime: startTime + duration,
          volumeLevel: existing?.volumeLevel != null ? existing.volumeLevel : 0.56,
          narrationText: textByNav[navId] || existing?.narrationText || "",
          voiceData: {
            provider: service,
            language: job.language || existing?.voiceData?.language || "",
            voiceId: job.voice || existing?.voiceData?.voiceId || "",
            // Sesi değiştirdiğimizde YENİ sesin adını yaz; eski ismi koruma —
            // yoksa loadSavedSound önce eski ismi gösterip (flicker) sonra
            // loadVoices voiceId'yi eşleştirip yeni isme döner.
            // voiceId'ye ASLA fallback yapma: voiceName bir görünen addır, ham
            // provider id'si (ör. "jk1pqab9OzEmS07w0u2T") isim olarak yazılırsa
            // kartta/sahne listesinde id görünür (bug).
            voiceName: job.voiceName || existing?.voiceData?.voiceName || "",
            gender: job.voiceGender || existing?.voiceData?.gender || "",
            narratorMode: "ai",
          },
        };

        if (existingIdx >= 0) {
          dispatch(editSound(navId, existingIdx, soundData));
          console.log("[NarrationJobTracker] editSound", navId, "idx", existingIdx, a.url);
        } else {
          dispatch(addSound(navId, soundData));
          console.log("[NarrationJobTracker] addSound", navId, a.url);
        }
        applied += 1;
      });

      return applied;
    },
    [store, dispatch]
  );

  const finish = useCallback(
    (jobId, status, extra) => {
      if (timersRef.current[jobId]) {
        clearTimeout(timersRef.current[jobId]);
        delete timersRef.current[jobId];
      }
      patchJob(jobId, { status, progress: status === "completed" ? 100 : undefined, ...extra });
      if (status === "completed") {
        timersRef.current[`${jobId}__dismiss`] = setTimeout(() => {
          if (mountedRef.current) removeJob(jobId);
        }, DONE_DISMISS_MS);
      }
    },
    [patchJob, removeJob]
  );

  const pollJob = useCallback(
    (job) => {
      const url = `${resolveJobsBase(job.baseUrl)}api/jobs/${job.jobId}`;
      console.log("[NarrationJobTracker] polling", url);
      let attempts = 0;
      const tick = async () => {
        if (!mountedRef.current) return;
        attempts += 1;
        if (attempts > MAX_POLL_ATTEMPTS) {
          console.warn("[NarrationJobTracker] giving up after max attempts", job.jobId);
          finish(job.jobId, "failed", {
            error: i18n.t("NarrateScope.Timeout") || "İşlem zaman aşımına uğradı.",
          });
          return;
        }
        try {
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${getToken()}`, Accept: "application/json" },
          });
          if (res.ok) {
            const data = await res.json();
            const status = data.status;
            patchJob(job.jobId, { progress: data.progress || 0 });

            if (status === "completed") {
              const applied = await applyResults({ ...job, result: data.result });
              // Sahnelere uygulandı → widget kartı kuyruktan Geçmiş'e geçsin.
              if (applied > 0) approveJob(job.jobId);
              const failedCount = (data.result?.failedNavIds || []).length;
              console.log("[NarrationJobTracker] completed — applied to", applied, "scene(s)");
              finish(job.jobId, "completed", { applied, failedCount });
              return;
            }
            if (status === "failed") {
              console.warn("[NarrationJobTracker] job failed", data.error);
              finish(job.jobId, "failed", { error: data.error || "" });
              return;
            }
          } else {
            console.warn("[NarrationJobTracker] poll HTTP", res.status, url);
          }
        } catch (error) {
          // Network blip — keep polling.
        }
        timersRef.current[job.jobId] = setTimeout(tick, POLL_INTERVAL);
      };
      tick();
    },
    [applyResults, finish, patchJob]
  );

  useEffect(() => {
    const onStart = (e) => {
      const d = (e && e.detail) || {};
      if (!d.jobId || startedRef.current.has(d.jobId)) return;
      console.log(
        "[NarrationJobTracker] started job",
        d.jobId,
        "scenes:",
        (d.scenes || []).map((s) => s && s.navId)
      );
      startedRef.current.add(d.jobId);
      const job = {
        jobId: d.jobId,
        baseUrl: d.baseUrl || "",
        trainingId: d.trainingId || "",
        scenes: Array.isArray(d.scenes) ? d.scenes : [],
        provider: d.provider || "",
        voice: d.voice || "",
        voiceName: d.voiceName || "",
        voiceGender: d.voiceGender || "",
        language: d.language || "",
        status: "processing",
        progress: 0,
        total: Array.isArray(d.scenes) ? d.scenes.length : 0,
      };
      setJobs((prev) => ({ ...prev, [d.jobId]: job }));
      pollJob(job);
    };
    window.addEventListener(NARRATE_ALL_EVENT, onStart);
    return () => window.removeEventListener(NARRATE_ALL_EVENT, onStart);
  }, [pollJob]);

  useEffect(() => {
    mountedRef.current = true;
    const timers = timersRef.current;
    return () => {
      mountedRef.current = false;
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  const jobList = Object.values(jobs);
  if (jobList.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 16,
        bottom: 16,
        zIndex: 1000001,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 340,
      }}
    >
      <style>{`@keyframes narrate-job-spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
      {jobList.map((job) => {
        const isDone = job.status === "completed";
        const isFailed = job.status === "failed";
        const accent = isFailed ? "#dc2626" : isDone ? "#16a34a" : "#6d5dfc";

        let text;
        if (isFailed) {
          text = i18n.t("NarrateScope.Failed") || "Seslendirme başarısız oldu.";
        } else if (isDone) {
          text =
            job.failedCount > 0
              ? i18n.t("NarrateScope.PartialDone", {
                  success: job.applied || 0,
                  failed: job.failedCount,
                }) || `${job.applied || 0} sahne güncellendi, ${job.failedCount} sahne başarısız.`
              : i18n.t("NarrateScope.Done", { count: job.applied || 0 }) ||
                `${job.applied || 0} sahne yeniden seslendirildi.`;
        } else {
          text =
            i18n.t("NarrateScope.Progress", { progress: job.progress || 0 }) ||
            `Seslendiriliyor... %${job.progress || 0}`;
        }

        return (
          <div
            key={job.jobId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: "#fff",
              border: `1px solid ${isFailed ? "#fecaca" : isDone ? "#bbf7d0" : "#e2e8f0"}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            }}
          >
            <span
              style={{
                flex: "0 0 auto",
                width: 24,
                height: 24,
                display: "grid",
                placeItems: "center",
                color: accent,
              }}
            >
              {isDone ? (
                <LuCheck size={18} />
              ) : isFailed ? (
                <LuTriangleAlert size={18} />
              ) : (
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "2px solid #e2e8f0",
                    borderTopColor: accent,
                    display: "inline-block",
                    animation: "narrate-job-spin 0.8s linear infinite",
                  }}
                />
              )}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                {isDone || isFailed
                  ? i18n.t("NarrateScope.DoneTitle") || "Seslendirme"
                  : i18n.t("NarrateScope.StartedTitle") || "Seslendirme başlatıldı"}
              </div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                {text}
                {isFailed && job.error ? (
                  <span style={{ display: "block", color: "#b91c1c", marginTop: 2 }}>
                    {mapAiProviderErrorMessage(job.error) || job.error}
                  </span>
                ) : null}
              </div>
            </div>
            {(isDone || isFailed) && (
              <button
                type="button"
                onClick={() => removeJob(job.jobId)}
                aria-label="Close"
                style={{
                  flex: "0 0 auto",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                  color: "#94a3b8",
                  padding: 0,
                }}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

NarrationJobTracker.propTypes = {
  store: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default NarrationJobTracker;
